import { useEffect, useState } from 'react';
import { patientApi } from '../services/api';
import { DEFAULT_CARE_PLAN_REVIEW_MONTHS } from '../utils/dateInputMask';

/**
 * PHA-1080: resolve a clinic's care plan review cycle, in months.
 *
 * This is the interval behind `care_plan_definitions.expiration_date` — the date
 * the Patients list column, the plan header and the printed PDF all call "Care
 * Plan Review Date". It used to be hardcoded to six everywhere; Zack asked for it
 * per clinic, because a CCM-style long-term-management practice runs a twelve-month
 * cycle where the PCMH recommendation is six.
 *
 * Resolution is nearest-ancestor on the backend, so a parent network can set the
 * cycle for all its clinics and a single clinic can still override it.
 *
 * Fails to DEFAULT_CARE_PLAN_REVIEW_MONTHS on every path — no org id, a fetch
 * error, a malformed response. That default is exactly what the field held before
 * this setting existed, so a blip degrades to today's behaviour rather than to a
 * missing or nonsensical review date. It also means the hook never returns
 * `undefined`: callers always have a number to hand to `defaultExpirationMasked`.
 *
 * `enabled` mirrors the `isOpen` guard the sibling org-flag effects in
 * PatientDetail use, so a closed chart does not fire the request.
 */
export const useCarePlanReviewMonths = (
  organizationId?: string | number | null,
  enabled = true,
): number => {
  const [months, setMonths] = useState<number>(DEFAULT_CARE_PLAN_REVIEW_MONTHS);

  useEffect(() => {
    if (!enabled) return;
    if (!organizationId) {
      setMonths(DEFAULT_CARE_PLAN_REVIEW_MONTHS);
      return;
    }

    let cancelled = false;
    // Same retry shape as getOrgNoTargetDatesEnabled's caller: a transient 502 on
    // this fan-out must not silently pin a twelve-month clinic back to six.
    const resolve = async (attempt = 1): Promise<void> => {
      try {
        const result = await patientApi.getOrgCarePlanReviewMonths(String(organizationId));
        if (cancelled) return;
        const value = Number(result?.care_plan_review_months);
        setMonths(Number.isFinite(value) && value > 0 ? value : DEFAULT_CARE_PLAN_REVIEW_MONTHS);
      } catch {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => { void resolve(attempt + 1); }, 400 * attempt);
          return;
        }
        setMonths(DEFAULT_CARE_PLAN_REVIEW_MONTHS);
      }
    };
    void resolve();

    return () => { cancelled = true; };
  }, [organizationId, enabled]);

  return months;
};
